import { now } from '../Clock.ts';
import { cloneState, type GameState } from '../game/GameState.ts';
import type { ExperimentEvent } from './Events.ts';

export interface LoggerOptions {
  /**
   * Attach a full GameState snapshot to every event. Snapshots are what make
   * offline analysis possible without re-running the simulation, but they are
   * also ~20x the size of a bare event.
   */
  attachGameState: boolean;
  /**
   * Ring-buffer cap. A 10-minute session at a few hundred events/second is well
   * under this; the cap only exists so an unattended run cannot exhaust memory.
   * Dropped events are counted and reported in the export header.
   */
  maxEvents: number;
}

export const DEFAULT_LOGGER_OPTIONS: LoggerOptions = {
  attachGameState: true,
  maxEvents: 500_000,
};

export interface SessionMetadata {
  sessionId: string;
  startedAt: string;
  startedAtPerf: number;
  [key: string]: unknown;
}

export interface SessionExport {
  format: 'flappy-hhi/session';
  version: 1;
  session: SessionMetadata;
  droppedEvents: number;
  events: ExperimentEvent[];
}

/**
 * Append-only event log for one experimental session.
 *
 * Everything that happens -- every controller decision, every stimulation
 * request, every detected input, every flap, every collision -- goes through
 * here, and only through here. That is what makes a session reconstructable
 * later, and it is why the logger has no dependency on the UI.
 */
export class EventLogger {
  private events: ExperimentEvent[] = [];
  private dropped = 0;
  private listeners = new Set<(e: ExperimentEvent) => void>();

  readonly options: LoggerOptions;
  readonly session: SessionMetadata;

  constructor(
    sessionId: string,
    options: Partial<LoggerOptions> = {},
    extraMetadata: Record<string, unknown> = {},
  ) {
    this.options = { ...DEFAULT_LOGGER_OPTIONS, ...options };
    this.session = {
      sessionId,
      startedAt: new Date().toISOString(),
      startedAtPerf: now(),
      ...extraMetadata,
    };
  }

  /** Subscribe to the live event stream (dashboard, latency tracker). */
  subscribe(fn: (e: ExperimentEvent) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  log(
    type: string,
    opts: {
      tick: number;
      timestamp?: number;
      simTimeMs?: number;
      gameState?: GameState;
      requestId?: string;
      trialId?: string;
      metadata?: Record<string, unknown>;
      /** Force a state snapshot even when `attachGameState` is off. */
      forceState?: boolean;
    },
  ): ExperimentEvent {
    const attach = opts.gameState && (this.options.attachGameState || opts.forceState);
    const event: ExperimentEvent = {
      sessionId: this.session.sessionId,
      timestamp: opts.timestamp ?? now(),
      tick: opts.tick,
      type,
    };
    if (opts.simTimeMs !== undefined) event.simTimeMs = opts.simTimeMs;
    if (opts.trialId) event.trialId = opts.trialId;
    if (opts.requestId) event.requestId = opts.requestId;
    if (attach && opts.gameState) event.gameState = cloneState(opts.gameState);
    if (opts.metadata) event.metadata = opts.metadata;

    if (this.events.length >= this.options.maxEvents) {
      this.events.shift();
      this.dropped++;
    }
    this.events.push(event);
    for (const fn of this.listeners) fn(event);
    return event;
  }

  all(): readonly ExperimentEvent[] {
    return this.events;
  }

  ofType(type: string): ExperimentEvent[] {
    return this.events.filter((e) => e.type === type);
  }

  /** Most recent `n` events, oldest first. */
  recent(n: number): ExperimentEvent[] {
    return this.events.slice(-n);
  }

  get count(): number {
    return this.events.length;
  }

  get droppedCount(): number {
    return this.dropped;
  }

  clear(): void {
    this.events = [];
    this.dropped = 0;
  }

  setMetadata(key: string, value: unknown): void {
    this.session[key] = value;
  }

  // ----------------------------------------------------------------- export

  toJSON(): SessionExport {
    return {
      format: 'flappy-hhi/session',
      version: 1,
      session: this.session,
      droppedEvents: this.dropped,
      events: this.events,
    };
  }

  toJSONString(pretty = false): string {
    return JSON.stringify(this.toJSON(), null, pretty ? 2 : 0);
  }

  /**
   * Flat CSV, one row per event.
   *
   * Game state is spread into fixed columns so pandas can read it without any
   * unpacking; anything irregular lands in a single JSON `metadata` column.
   */
  toCSV(): string {
    const columns = [
      'sessionId', 'trialId', 'type', 'timestamp', 'simTimeMs', 'tick', 'requestId',
      'birdY', 'birdVelocityY', 'nextPipeX', 'nextPipeGapTop', 'nextPipeGapBottom',
      'score', 'alive', 'flapped', 'metadata',
    ];
    const rows: string[] = [columns.join(',')];
    for (const e of this.events) {
      const g = e.gameState;
      rows.push(
        [
          csv(e.sessionId),
          csv(e.trialId ?? ''),
          csv(e.type),
          num(e.timestamp),
          num(e.simTimeMs),
          num(e.tick),
          csv(e.requestId ?? ''),
          num(g?.birdY),
          num(g?.birdVelocityY),
          num(g?.nextPipeX),
          num(g?.nextPipeGapTop),
          num(g?.nextPipeGapBottom),
          num(g?.score),
          g ? (g.alive ? '1' : '0') : '',
          g ? (g.flapped ? '1' : '0') : '',
          csv(e.metadata ? JSON.stringify(e.metadata) : ''),
        ].join(','),
      );
    }
    return rows.join('\n');
  }
}

function csv(value: string): string {
  if (value === '') return '';
  if (/[",\n]/.test(value)) return `"${value.replace(/"/g, '""')}"`;
  return value;
}

function num(value: number | undefined): string {
  if (value === undefined || value === null || Number.isNaN(value)) return '';
  return Number.isInteger(value) ? String(value) : value.toFixed(4);
}
